import { ChangeEvent, FC, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { skipToken } from '@reduxjs/toolkit/query';
import { useSnackbar } from 'notistack';

import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  SelectChangeEvent,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  NorthEast as NorthEastIcon,
  ReceiptLong as ReceiptLongIcon,
  SouthWest as SouthWestIcon,
  Visibility as VisibilityIcon,
  ContentCopy as ContentCopyIcon,
  Add as CreateIcon,
  Edit as EditIcon,
} from '@mui/icons-material';

import { Table } from '../../../components/table/table';
import { HeadCell, Row, RowCell } from '../../../components/table/types';
import { ActionModal, ActionModalProps, WalletAction } from './components';

import {
  CreateWalletMerchantDTO,
  type GetWalletBlockchainBalanceResult,
  UpdateWalletProviderDTO,
  useCreateWalletMerchantMutation,
  useDepositFiatWalletMutation,
  useGetProfileWalletsQuery,
  useLazyGetWalletBlockchainBalanceQuery,
  useUpdateWalletProviderMutation,
  useWithdrawFiatWalletMutation,
} from '../../../api';
import { Blockchain } from '../../../enums';
import { UTCToDateTime } from '../../../utils/dates';
import { RouterPaths } from '../../../router/paths';
import { permissionsCheck } from '../../../utils/permission';
import { formatAmount, formatBlockchainAmountWithCurrency, formatShort } from '../../../utils';
import { layoutAction } from '../../../store/sideBarStore/layoutStore';
import { useAppActions } from '../../../utils/hooks';
import { CreateModal } from './components/CreateModal';
import { useGetProvidersQuery } from '../../../api/controllers/providers';
import { EditProviderModal } from './components/EditProviderModal';

export const Wallets: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { profileUid } = useParams<'profileUid'>();
  const { enqueueSnackbar } = useSnackbar();
  const hasPermissionToDeposit = permissionsCheck(['FIAT_DEPOSIT']);
  const hasPermissionToWithdraw = permissionsCheck(['FIAT_WITHDRAWAL']);
  const hasPermissionToEditWallets = permissionsCheck(['WRITE_WALLETS']);

  // api
  const {
    data: wallets,
    isLoading: isWalletsLoading,
    refetch,
  } = useGetProfileWalletsQuery(profileUid ? { profileUid } : skipToken);
  const [depositFiat, { isLoading: isDepositFiatLoading }] = useDepositFiatWalletMutation();
  const [createWallet, { isLoading: isCreateWalletLoading }] = useCreateWalletMerchantMutation();
  const [withdrawFiat, { isLoading: isWithdrawFiatLoading }] = useWithdrawFiatWalletMutation();
  const [updateProvider, { isLoading: isUpdateProviderLoading }] =
    useUpdateWalletProviderMutation();
  const [
    getBlockchainBalance,
    {
      isLoading: isBalanceGetLoading,
      isFetching: isBalanceGetFetching,
      originalArgs: originalBalanceArgs,
    },
  ] = useLazyGetWalletBlockchainBalanceQuery();

  // variables
  const isLoading =
    isWalletsLoading || isDepositFiatLoading || isWithdrawFiatLoading || isBalanceGetLoading;
  const [blockchainBalances, setBlockchainBalances] = useState<Record<
    string,
    GetWalletBlockchainBalanceResult
  > | null>(null);

  const [isEditProviderModalShown, setIsEditProviderModalShown] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState('');
  // history modal logic
  const [actionModalShown, setActionModalShown] = useState(false);
  const [currentAction, setCurrentAction] = useState<WalletAction | ''>('');
  const [currentWalletUid, setCurrentWalletUid] = useState<string>('');
  const openActionModal = (action: WalletAction, walletUid: string) => {
    setActionModalShown(true);
    setCurrentAction(action);
    setCurrentWalletUid(walletUid);
  };
  const closeActionModal = () => {
    setActionModalShown(false);
    setCurrentAction('');
    setCurrentWalletUid('');
  };

  // action handler
  const runAction: ActionModalProps['onSuccess'] = async (values, resetFields) => {
    if (!values) return;

    const args = { walletUid: currentWalletUid, body: values };
    const request = currentAction === 'deposit' ? depositFiat : withdrawFiat;

    try {
      await request(args).unwrap();
      closeActionModal();
      resetFields();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };
  const onUpdateProvider = async (
    values: UpdateWalletProviderDTO['body'],
    resetFields: () => void
  ) => {
    if (!values) return;

    const args = { walletUid: currentWalletUid, body: values };

    try {
      await updateProvider(args).unwrap();
      setSelectedProvider('');
      setCurrentWalletUid('');
      setIsEditProviderModalShown(false);
      resetFields();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  // get blockchain balance handler
  const getBalance = async (walletUid: string) => {
    if (!walletUid) return;

    const args = { walletUid };

    try {
      const res = await getBlockchainBalance(args).unwrap();
      setBlockchainBalances((prevState) => ({
        ...prevState,
        [walletUid]: { ...res },
      }));
    } catch {
      // @ts-ignore
      enqueueSnackbar(t('profile-wallets.actions.show-balance-error'), { variant: 'error' });
    }
  };

  // show action button
  const { setSpeedDialButtonComponent, removeSpeedDialButtonContent } = useAppActions(layoutAction);
  useEffect(() => {
    setSpeedDialButtonComponent([{ icon: <CreateIcon />, onClick: openCreateModal }]);

    return () => {
      removeSpeedDialButtonContent();
    };
  }, []);

  // add document modal logic
  const [createModalShown, setCreateModalShown] = useState(false);
  const openCreateModal = () => {
    setCreateModalShown(true);
  };
  const closeCreateModal = () => {
    setCreateModalShown(false);
  };

  const [editFields, setEditFields] = useState<CreateWalletMerchantDTO['body'] | null>(null);
  const handleEditFields = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent
  ) => {
    const name = e.target.name;
    const value = e.target.value;

    // @ts-ignore
    setEditFields((prevState) => {
      if (!prevState) {
        return {
          [name]: value,
        };
      }

      return {
        ...prevState,
        [name]: value,
      };
    });
  };
  // create fee handler
  const createFee = async () => {
    if (!editFields || !profileUid) return;

    const args = {
      profile_uid: profileUid,
      body: {
        coin_id: editFields.coin_id,
      },
    };

    try {
      await createWallet(args).unwrap();
      closeCreateModal();
      refetch();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  // table cells and rows
  const headCells: HeadCell[] = [
    {
      id: '1',
      label: t('profile-wallets.head-cells.created-at'),
    },
    // {
    //   id: '1',
    //   label: t('profile-wallets.head-cells.wallet-uid'),
    // },
    {
      id: '2',
      label: t('profile-wallets.head-cells.coin'),
    },
    {
      id: '3',
      label: t('profile-wallets.head-cells.type'),
    },
    {
      id: '4',
      label: t('profile-wallets.head-cells.provider'),
    },
    {
      id: '5',
      label: t('profile-wallets.head-cells.blockchain-address'),
    },
    {
      id: '6',
      label: t('profile-wallets.head-cells.balance'),
      align: 'right',
    },
    {
      id: '7',
      label: t('profile-wallets.head-cells.blocked-amount'),
      align: 'right',
    },
    {
      hidden: !hasPermissionToEditWallets,
      id: '8',
      label: t('profile-wallets.head-cells.actions'),
    },
  ];

  const rows: Row[] | undefined = wallets?.wallets?.map((wallet) => {
    const { uid, created_at, coin, type, provider, balance, blocked_amount, blockchain_address } =
      wallet;
    const isFiat = coin.blockchain === Blockchain.FIAT;

    const cells: RowCell[] = [
      {
        name: 'created_at',
        value: created_at,
        formatting: (value: string) => {
          return UTCToDateTime(value);
        },
      },
      // {
      //   name: 'uid',
      //   value: formatShort(uid),
      // },
      {
        name: 'coin',
        value: coin.name,
      },
      {
        name: 'type',
        value: type,
        formatting: (value: string) => {
          return t(`common.wallet-type.${value}`);
        },
      },
      {
        name: 'provider',
        value: (
          <Stack direction="row" alignItems="center">
            {provider}
            <IconButton
              aria-label="copy-blockchain-address"
              color="info"
              onClick={() => {
                setSelectedProvider(provider);
                setCurrentWalletUid(uid);
                setIsEditProviderModalShown(true);
              }}
              disabled={isLoading}
            >
              <EditIcon />
            </IconButton>
          </Stack>
        ),
      },
      {
        name: 'uid_and_blockchain_address',
        component: (
          <Stack direction="column" spacing={1}>
            {
              <Stack>
                {blockchain_address && (
                  <Stack direction="row" alignItems="center" fontSize={12}>
                    {blockchain_address}
                    <IconButton
                      aria-label="copy-blockchain-address"
                      color="info"
                      onClick={() => navigator.clipboard.writeText(blockchain_address)}
                      disabled={isLoading}
                    >
                      <ContentCopyIcon />
                    </IconButton>
                  </Stack>
                )}
                <Box
                  display={'flex'}
                  flexDirection={'row'}
                  borderTop={blockchain_address && '1px solid rgba(224, 224, 224, 1)'}
                  paddingTop={blockchain_address && 1}
                  gap={1}
                >
                  <Typography fontSize={12} fontWeight={600}>
                    {t('profile-wallets.head-cells.wallet-uid')}
                  </Typography>
                  <Typography fontSize={12}>{uid}</Typography>
                </Box>
              </Stack>
            }
          </Stack>
        ),
      },
      {
        name: 'balance',
        value: balance,
        align: 'right',
        component: (
          <Stack direction="column">
            <Stack direction="row" alignItems="center" spacing={2}>
              {coin.blockchain !== Blockchain.FIAT && blockchain_address && (
                <Tooltip title={t('profile-wallets.actions.show-balance')}>
                  <IconButton
                    aria-label="show-balance"
                    color="info"
                    onClick={() => getBalance(uid)}
                    disabled={isLoading || isBalanceGetFetching}
                  >
                    <VisibilityIcon />
                  </IconButton>
                </Tooltip>
              )}

              <Box flexGrow={1}>{formatAmount(balance)}</Box>
            </Stack>

            {(isBalanceGetLoading || isBalanceGetFetching) &&
            originalBalanceArgs?.walletUid === uid ? (
              <Box style={{ display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={24} />
              </Box>
            ) : // eslint-disable-next-line no-prototype-builtins
            blockchainBalances?.hasOwnProperty(uid) ? (
              <Stack direction="column">
                <Box>
                  <Box component="span" fontWeight={600}>
                    {formatBlockchainAmountWithCurrency(
                      blockchainBalances[uid].balance,
                      blockchainBalances[uid].coin.code
                    )}
                  </Box>
                </Box>

                <Box>
                  <Box component="span" fontWeight={600}>
                    {formatBlockchainAmountWithCurrency(
                      blockchainBalances[uid].fee_coin_balance,
                      blockchainBalances[uid].fee_coin_code
                    )}
                  </Box>
                </Box>
              </Stack>
            ) : null}
          </Stack>
        ),
      },
      {
        name: 'blocked_amount',
        value: formatAmount(blocked_amount),
        align: 'right',
      },
      {
        name: 'actions',
        component: (
          <Stack
            direction="row"
            spacing={2}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <Tooltip title={t('profile-wallets.actions.show-transactions')}>
              <IconButton
                aria-label="show-transactions"
                color="info"
                onClick={() =>
                  navigate(`${RouterPaths.Profiles}/${profileUid}/transactions`, {
                    state: { filterWalletUid: uid },
                  })
                }
                disabled={isLoading || isBalanceGetFetching}
              >
                <ReceiptLongIcon />
              </IconButton>
            </Tooltip>

            {isFiat && hasPermissionToEditWallets && (
              <>
                {hasPermissionToDeposit && (
                  <Tooltip title={t('profile-wallets.actions.deposit.name')}>
                    <IconButton
                      aria-label="deposit"
                      color="success"
                      onClick={() => openActionModal('deposit', uid)}
                      disabled={isLoading || isBalanceGetFetching}
                    >
                      <SouthWestIcon />
                    </IconButton>
                  </Tooltip>
                )}

                {hasPermissionToWithdraw && (
                  <Tooltip title={t('profile-wallets.actions.withdraw.name')}>
                    <IconButton
                      aria-label="withdraw"
                      color="error"
                      onClick={() => openActionModal('withdraw', uid)}
                      disabled={isLoading || isBalanceGetFetching}
                    >
                      <NorthEastIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            )}
          </Stack>
        ),
      },
    ];

    return { cells };
  });

  return (
    <>
      <Grid size={{ xs: 12 }}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
          <Table headCells={headCells} rows={rows} />
        </Paper>
      </Grid>

      <ActionModal
        open={actionModalShown}
        onClose={closeActionModal}
        walletAction={currentAction}
        onSuccess={runAction}
        isLoading={isLoading}
      />

      {selectedProvider && (
        <EditProviderModal
          open={isEditProviderModalShown}
          onClose={() => setIsEditProviderModalShown(false)}
          onSuccess={onUpdateProvider}
          isLoading={isLoading}
          provider={selectedProvider}
        />
      )}

      <CreateModal
        open={createModalShown}
        onClose={closeCreateModal}
        onCreate={createFee}
        fields={editFields}
        fieldsHandler={handleEditFields}
        isLoading={isCreateWalletLoading}
      />
    </>
  );
};
